import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  useFormContext,
  FormDescription,
} from '@predoc/cow';
import { format, parseISO } from 'date-fns';
import { useCallback } from 'react';
import {
  ProviderFacilityFormData,
  ProviderFacilityFormFieldProps,
} from './validation';
import { FacilityDropdown } from './FacilityDropdown';
import { ProviderSearchResult } from '@/feature/patients/types';
import { DatePickerInput } from '@/components/DatePickerInput';
import { CityAutocomplete } from '@/components/CityAutocomplete';
import { handlePhoneDigitsChange } from '@/utils/phoneForm';
import { parsePhoneNumber } from '@/utils/phoneUtils';

export const ProviderFacilityFormFields = ({
  onFieldChange,
  lockSearchDuringFormPopulation,
  unlockSearchAfterFormPopulation,
}: {
  onFieldChange: (field: string, value: string) => void;
  lockSearchDuringFormPopulation: () => void;
  unlockSearchAfterFormPopulation: () => void;
}) => {
  const { control, watch, reset, getValues, setValue, clearErrors } =
    useFormContext();

  const handleCitySelect = useCallback(
    (_city: string, state: string) => {
      // Auto-fill state when city is selected
      // City is already set via field.onChange in CityAutocomplete
      setValue('state', state, {
        shouldValidate: true,
      });
      onFieldChange('state', state);
    },
    [setValue, onFieldChange],
  );

  const handleFacilityChange = (facility: ProviderSearchResult | undefined) => {
    const currentValues = getValues();

    lockSearchDuringFormPopulation();

    reset({
      ...currentValues,
      facilityName: facility?.facilityName || '',
      providerName: facility ? '' : currentValues.providerName,
      address1: facility?.address?.address1 || '',
      address2: facility?.address?.address2 || '',
      city: facility?.address?.city || '',
      state: facility?.address?.state || '',
      postalCode: facility?.address?.postalCode || '',
      phoneNumber: parsePhoneNumber(facility?.phoneNumbers?.[0] || ''),
      faxNumber: parsePhoneNumber(facility?.faxNumbers?.[0] || ''),
      specialty: facility?.specialties?.join(', ') || '',
    });

    unlockSearchAfterFormPopulation();
    // Trigger provider search for individuals within the selected facility
    if (facility?.facilityName) {
      onFieldChange('facilityName', facility.facilityName);
    }
  };

  const recordsRequested = watch('recordsRequested');
  const serviceDateRange = watch('serviceDateRange');
  const showMedicalRecordDetails = recordsRequested === 'specific';
  const showCustomDateRange = serviceDateRange === 'custom';

  return (
    <div className="space-y-6 p-4">
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900">
          Provider/Facility *
        </h2>
        <p className="text-sm text-gray-600">
          Select a provider and/or facility
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name="providerName"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'providerName'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Provider Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Add a provider..."
                    className="text-sm"
                    maxLength={255}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e);
                      onFieldChange('providerName', e.target.value);
                      if (e.target.value.trim()) {
                        clearErrors(['providerName', 'facilityName']);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>ex. Janet Doe</FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="facilityName"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'facilityName'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Facility Name</FormLabel>
                <FormControl>
                  <FacilityDropdown
                    value={field.value}
                    onChange={(facility) => {
                      field.onChange(facility?.facilityName || '');
                      handleFacilityChange(facility);
                    }}
                    onInputChange={(value) => {
                      field.onChange(value);
                      onFieldChange('facilityName', value);
                      if (value.trim()) {
                        clearErrors(['providerName', 'facilityName']);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>ex. Greenfield Medical Group</FormDescription>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name="specialty"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'specialty'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Specialty</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. Family Medicine"
                    className="text-sm"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name="address1"
            render={({ field }: ProviderFacilityFormFieldProps<'address1'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Street Address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. 123 Maple Avenue"
                    className="text-sm"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="address2"
            render={({ field }: ProviderFacilityFormFieldProps<'address2'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Street Address 2</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. Unit 100"
                    className="text-sm"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name="city"
            render={({ field }: ProviderFacilityFormFieldProps<'city'>) => (
              <FormItem className="col-span-4">
                <FormLabel>City</FormLabel>
                <FormControl>
                  <CityAutocomplete
                    value={field.value || ''}
                    onChange={(value) => {
                      field.onChange(value);
                      onFieldChange('city', value);
                    }}
                    onCitySelect={handleCitySelect}
                    placeholder="ex. Greenville"
                    className="text-sm"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="state"
            render={({ field }: ProviderFacilityFormFieldProps<'state'>) => (
              <FormItem className="col-span-4">
                <FormLabel>State/Province</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. NY"
                    className="text-sm"
                    maxLength={2}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e);
                      onFieldChange('state', e.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="postalCode"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'postalCode'>) => (
              <FormItem className="col-span-4">
                <FormLabel>Postal/ZIP Code</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. 12345"
                    className="text-sm"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e);
                      onFieldChange('postalCode', e.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name="phoneNumber"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'phoneNumber'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. 1234567890"
                    className="text-sm"
                    inputMode="numeric"
                    onChange={handlePhoneDigitsChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="faxNumber"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'faxNumber'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Fax Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="ex. 1234567890"
                    className="text-sm"
                    inputMode="numeric"
                    onChange={handlePhoneDigitsChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-12 items-start gap-4">
          <FormField
            control={control}
            name="recordsRequested"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'recordsRequested'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Records Requested *</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(
                      val: ProviderFacilityFormData['recordsRequested'],
                    ) => {
                      field.onChange(val);
                      if (val === 'complete') {
                        setValue('medicalRecordDetails', null);
                      }
                    }}
                    value={field.value}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="complete" id="complete" />
                      <label htmlFor="complete" className="text-sm">
                        Complete Medical Records
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id="specific" />
                      <label htmlFor="specific" className="text-sm">
                        Specific Medical Records
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>

                {showMedicalRecordDetails && (
                  <div className="mt-4">
                    <FormField
                      control={control}
                      name="medicalRecordDetails"
                      render={({
                        field,
                      }: ProviderFacilityFormFieldProps<'medicalRecordDetails'>) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Medical Record Details *
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Records for cancer treatment of lung"
                              rows={4}
                              className="text-sm"
                            />
                          </FormControl>
                          <FormMessage className="text-destructive" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="serviceDateRange"
            render={({
              field,
            }: ProviderFacilityFormFieldProps<'serviceDateRange'>) => (
              <FormItem className="col-span-6">
                <FormLabel>Service Date Range *</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="6months" id="6months" />
                      <label htmlFor="6months" className="text-sm">
                        Last 6 Months
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1year" id="1year" />
                      <label htmlFor="1year" className="text-sm">
                        Last Year
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="custom" />
                      <label htmlFor="custom" className="text-sm">
                        Custom
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>

                {showCustomDateRange && (
                  <div className="mt-4 grid grid-cols-2 items-start gap-4">
                    <FormField
                      control={control}
                      name="serviceStartDate"
                      render={({
                        field,
                      }: ProviderFacilityFormFieldProps<'serviceStartDate'>) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Service Start Date *
                          </FormLabel>
                          <FormControl>
                            <DatePickerInput
                              onChange={(date: Date | undefined) => {
                                field.onChange(
                                  date ? format(date, 'yyyy-MM-dd') : '',
                                );
                              }}
                              value={
                                field.value ? parseISO(field.value) : undefined
                              }
                              placeholder="Select start date"
                              disableDates="future"
                            />
                          </FormControl>
                          <FormMessage className="text-destructive" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="serviceEndDate"
                      render={({
                        field,
                      }: ProviderFacilityFormFieldProps<'serviceEndDate'>) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Service End Date *
                          </FormLabel>
                          <FormControl>
                            <DatePickerInput
                              onChange={(date: Date | undefined) => {
                                field.onChange(
                                  date ? format(date, 'yyyy-MM-dd') : '',
                                );
                              }}
                              value={
                                field.value ? parseISO(field.value) : undefined
                              }
                              placeholder="Select end date"
                              disableDates="future"
                            />
                          </FormControl>
                          <FormMessage className="text-destructive" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
